"use client"

import { useEffect, useState } from "react"

import {
  Button,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Textarea,
} from "@coval/ui"
import { BookOpenText } from "lucide-react"

export function SummaryMemoryMenu({
  canEdit,
  preferencesText,
  onChange,
  onClear,
}: {
  canEdit: boolean
  preferencesText: string
  onChange: (preferencesText: string) => void
  onClear: () => void
}) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState(preferencesText)
  const hasMemory = preferencesText.trim().length > 0

  useEffect(() => {
    if (!open) {
      setDraft(preferencesText)
    }
  }, [open, preferencesText])

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (nextOpen) {
      setDraft(preferencesText)
    }
  }

  const handleSave = () => {
    if (!canEdit) {
      return
    }
    onChange(draft)
    setOpen(false)
  }

  const handleClear = () => {
    if (!canEdit) {
      return
    }
    setDraft("")
    onClear()
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Sofia summary memory"
          aria-label="Edit Sofia summary memory"
          className={cn(
            "flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
            hasMemory
              ? "bg-surface-2 text-coval-text"
              : "text-coval-text-muted hover:bg-surface-2 hover:text-coval-text",
          )}
        >
          <BookOpenText className="h-4 w-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-coval-text">
            Summary memory
          </h2>
          <Textarea
            aria-label="Sofia summary memory"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            rows={5}
            placeholder="Prioritize latency, disconnects, and GR Compliance Breach for got-rate-prod."
            className="min-h-28 text-xs"
            disabled={!canEdit}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClear}
              disabled={!canEdit || (!draft.trim() && !hasMemory)}
            >
              Clear
            </Button>
            <Button type="button" onClick={handleSave} disabled={!canEdit}>
              Save
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
